import * as T from 'three';
import { Component } from './live-codec.js';
import { modelBrushes } from './debug-rendering.js';

// Swept point against a convex BSP brush. This also catches thin triggers when
// boosting past them between frames; an AABB alone would accept empty corners.
export function brushTouch(start, end, planes) {
    let enter = 0, leave = 1;
    for (const { normal, distance } of planes) {
        const a = start.dot(normal) - distance, b = end.dot(normal) - distance;
        if (a > 0 && b > 0) return false;
        if (a <= 0 && b <= 0) continue;
        const fraction = a / (a - b);
        if (a > b) enter = Math.max(enter, fraction); else leave = Math.min(leave, fraction);
        if (enter > leave) return false;
    }
    return true;
}
export class ViewerTeleports {
    constructor(models, visibility) { this.models = models; this.visibility = visibility; this.brushes = new Map(); this.inside = new Set(); }
    geometry(id) {
        if (this.brushes.has(id)) return this.brushes.get(id);
        const model = this.models[id], v = this.visibility, brushes = [];
        if (model) for (const brush of modelBrushes(v, model.headnode)) {
            const first = v.brushes.i32(brush * 12), count = v.brushes.i32(brush * 12 + 4);
            if (count < 4 || count > 128) continue;
            brushes.push(Array.from({ length: count }, (_, i) => {
                const offset = v.brushSides.u16((first + i) * 8) * 20;
                return { normal: new T.Vector3(...v.planes.vec(offset)), distance: v.planes.f32(offset + 12) };
            }));
        }
        this.brushes.set(id, brushes); return brushes;
    }
    frame(view, start, end, scene) {
        if (!scene?.owned) { this.inside.clear(); return false; }
        const touching = new Set(), candidates = []; let selected, selectedKey;
        for (const entity of scene.entities.values()) {
            const state = entity.state.components[Component.Teleport];
            if (!state || !(state.flags & 1)) continue;
            const path = scene.dictionary.get(`1:${entity.state.components[Component.Appearance]?.model}`);
            if (!/^\*\d+$/.test(path ?? '')) continue;
            entity.object.updateWorldMatrix(true, false);
            const inverse = entity.object.matrixWorld.clone().invert(), a = start.clone().applyMatrix4(inverse), b = end.clone().applyMatrix4(inverse);
            const brushes = this.geometry(Number(path.slice(1)));
            candidates.push({ key: entity.key, inverse, brushes });
            if (brushes.some(planes => brushTouch(b, b, planes))) touching.add(entity.key);
            if (!selected && !this.inside.has(entity.key) && brushes.some(planes => brushTouch(a, b, planes))) { selected = state; selectedKey = entity.key; }
        }
        this.inside = touching;
        if (!selected) return false;
        const destination = new T.Vector3(...selected.destination);
        if (selected.flags & 8) destination.add(end).sub(new T.Vector3(...selected.landmark));
        else destination.z += 68;
        touching.add(selectedKey);
        for (const candidate of candidates) {
            const point = destination.clone().applyMatrix4(candidate.inverse);
            if (candidate.brushes.some(planes => brushTouch(point, point, planes))) touching.add(candidate.key);
        }
        view.camera.position.copy(destination);
        if (!(selected.flags & (2 | 4 | 8))) {
            view.yaw = selected.angles[1] * Math.PI / 180;
            view.pitch = -selected.angles[0] * Math.PI / 180;
        }
        // At most one teleport per frame. Remain latched while inside either
        // trigger, including overlapping portals and a destination in itself.
        return true;
    }
}
